import { FC } from 'react';
import {
    Tooltip,
    TooltipProps,
    Typography,
    TypographyProps,
    Card,
    CardContent,
    Box,
    LinearProgress,
    linearProgressClasses,
    styled,
} from '@mui/material';
import {
    AdminPanelSettingsOutlined as AdminPanelSettingsOutlinedIcon,
    HelpOutlineOutlined as HelpOutlineOutlinedIcon,
} from '@mui/icons-material';
import { History as HistoryIcon } from '@mui/icons-material';
import Column from '../../../../../components/Column';
import Row from '../../../../../components/Row';

import { Campaign, CampaignFlight } from '../../../../../types/Campaign';
import Utils from '../../../../../components/Utils';
import CampaignPacingUtils from '../../../../../utils/CampaignPacingUtils';
import IconContainer from '../../../../../components/Icon/IconContainer';
import { CampaignPacingLabels } from './CampaignPacingCard';
import { useUserContext } from '../../../../../hooks/Contexts/UserContext';

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
    height: 6,
    borderRadius: theme.spacing(1),
    [`&.${linearProgressClasses.root}`]: {
        backgroundColor: theme.palette.grey[300],
    },
}));

interface TooltipTypographyProps extends TypographyProps {
    tooltip?: TooltipProps['title'];
}

const FlightsSummaryTitle: FC<TooltipTypographyProps> = ({ children, tooltip, ...props }) => {
    return (
        <Row alignItems="center" gap={0.5}>
            <Typography variant="caption" color="text.primary" {...props}>
                {children}
            </Typography>

            {tooltip && (
                <Tooltip title={tooltip}>
                    <HelpOutlineOutlinedIcon color="action" sx={{ fontSize: 16 }} />
                </Tooltip>
            )}
        </Row>
    );
};

const CampaignFlightsSummarySubtitle: FC<TooltipTypographyProps> = ({ children, tooltip, ...props }) => {
    return (
        <Row alignItems="center" gap={0.5}>
            <Typography variant="subtitle2" fontWeight={500} {...props}>
                {children}
            </Typography>

            {tooltip && (
                <Tooltip title={tooltip}>
                    <HelpOutlineOutlinedIcon color="action" sx={{ fontSize: 16 }} />
                </Tooltip>
            )}
        </Row>
    );
};

interface FlightsSummaryCardProps {
    campaign: Campaign;
    liveFlights?: CampaignFlight[];
    liveFlightNumbers?: number[];
}

const CampaignFlightsSummaryCard: FC<FlightsSummaryCardProps> = ({
    campaign,
    liveFlights = [],
    liveFlightNumbers = [],
}) => {
    const budgetPacingPercentage = campaign?.budgetPacing ? Utils.round(campaign.budgetPacing * 100, 0) : 0;
    const pacingColor = CampaignPacingUtils.getPacingColor(budgetPacingPercentage);
    const pacingLabel = CampaignPacingLabels[CampaignPacingUtils.identifyPacing(budgetPacingPercentage)];
    const { userContext } = useUserContext();

    const totalBudget = liveFlights.reduce((sum, f) => sum + (f.budgetAmount ?? 0), 0);
    const totalDspBudget = liveFlights.reduce((sum, f) => sum + (f.dspBudget ?? 0), 0);
    const totalImpressionGoal = liveFlights.reduce((sum, f) => sum + (f.impressionGoal ?? 0), 0);
    const startDate =
        liveFlights.length > 0
            ? liveFlights.reduce(
                  (min, f) => (new Date(f.startDateTime) < new Date(min) ? f.startDateTime : min),
                  liveFlights[0].startDateTime
              )
            : null;
    const endDate =
        liveFlights.length > 0
            ? liveFlights.reduce(
                  (max, f) => (new Date(f.endDateTime) > new Date(max) ? f.endDateTime : max),
                  liveFlights[0].endDateTime
              )
            : null;

    const AdminOnlyIcon = () => {
        return (
            <Tooltip title="Visible to Admins Only">
                <AdminPanelSettingsOutlinedIcon
                    color="primary"
                    sx={{ fontSize: 17, ml: 1, verticalAlign: 'text-bottom' }}
                />
            </Tooltip>
        );
    };

    return (
        <Card variant="outlined">
            <CardContent>
                <Row gap={3} alignItems="center">
                    <Column gap={1}>
                        <FlightsSummaryTitle>Current Flight</FlightsSummaryTitle>
                        <CampaignFlightsSummarySubtitle>
                            {liveFlightNumbers.length > 0 ? liveFlightNumbers.map((n) => `${n}`).join(', ') : ' - '}
                        </CampaignFlightsSummarySubtitle>
                    </Column>

                    <Column gap={1}>
                        <FlightsSummaryTitle>Budget</FlightsSummaryTitle>
                        <CampaignFlightsSummarySubtitle>
                            {totalBudget > 0 ? Utils.formatCurrency(totalBudget) : ' - '}
                        </CampaignFlightsSummarySubtitle>
                    </Column>

                    {userContext.isAdmin() && (
                        <Column gap={1}>
                            <FlightsSummaryTitle>
                                DSP Budget
                                <AdminOnlyIcon />
                            </FlightsSummaryTitle>
                            <CampaignFlightsSummarySubtitle>
                                {totalDspBudget > 0 ? Utils.formatCurrency(totalDspBudget) : ' - '}
                            </CampaignFlightsSummarySubtitle>
                        </Column>
                    )}

                    <Column gap={1}>
                        <FlightsSummaryTitle>Impression Goal</FlightsSummaryTitle>
                        <CampaignFlightsSummarySubtitle>
                            {totalImpressionGoal > 0 ? totalImpressionGoal.toLocaleString() : ' - '}
                        </CampaignFlightsSummarySubtitle>
                    </Column>

                    <Column gap={1}>
                        <FlightsSummaryTitle tooltip="This is the start date of the earliest live flight.">
                            Start Date
                        </FlightsSummaryTitle>
                        <CampaignFlightsSummarySubtitle>
                            {startDate ? Utils.formatESTDate(startDate) : ' - '}
                        </CampaignFlightsSummarySubtitle>
                    </Column>

                    <Column gap={1}>
                        <FlightsSummaryTitle tooltip="This is the end date of the latest live flight.">
                            End Date
                        </FlightsSummaryTitle>
                        <CampaignFlightsSummarySubtitle>
                            {endDate ? Utils.formatESTDate(endDate) : ' - '}
                        </CampaignFlightsSummarySubtitle>
                    </Column>

                    {/* Inline Pacing Section */}
                    <Box sx={{ flex: 1, minWidth: 250 }}>
                        <Column gap={1}>
                            <Row alignItems="center" gap={0.5}>
                                <IconContainer icon={HistoryIcon} color={pacingColor} sx={{ width: 20, height: 20 }} />
                                <Typography variant="caption" color="text.primary" fontWeight={500}>
                                    {pacingLabel}
                                </Typography>
                            </Row>

                            <Row alignItems="center" gap={1}>
                                <Box sx={{ flex: 1 }}>
                                    <BorderLinearProgress
                                        variant="determinate"
                                        value={Math.min(budgetPacingPercentage, 100)}
                                        color={pacingColor}
                                    />
                                </Box>
                                <Typography
                                    variant="subtitle2"
                                    color="text.primary"
                                    fontWeight={500}
                                    sx={{ minWidth: 45 }}
                                >
                                    {budgetPacingPercentage.toFixed(0)}%
                                </Typography>
                            </Row>
                        </Column>
                    </Box>
                </Row>
            </CardContent>
        </Card>
    );
};

export type { FlightsSummaryCardProps };
export default CampaignFlightsSummaryCard;
